import { useEffect } from 'react';
import ToastModule from '@modules/ToastModule';

type HammerToastDetail = {
  id?: string;
  eventType: 'add' | 'update' | 'dismiss';
  status?: 'success' | 'warning' | 'danger' | 'info';
  title?: string;
  message?: string;
  duration?: number | false;
};

// Long enough to read as "until closed", matching the other sticky toasts
// (missed call, force away).
const UNTIL_CLOSED_DURATION_MS = 8 * 3600 * 1000; // 8h

function handleHammerToast(e: Event) {
  const detail = (e as CustomEvent<HammerToastDetail>).detail;
  // anvil2's `toast.dismiss(id)` sends the id the toast was added with.
  if (detail?.eventType === 'dismiss') {
    if (detail.id) ToastModule.hideToastById(detail.id);
    return;
  }
  if (detail?.eventType !== 'add') return;
  if (!detail.title) return;

  const status = detail.status === 'danger' ? 'critical' : detail.status;
  // anvil2 uses `duration: false` for a toast that stays until closed.
  const duration =
    detail.duration === false ? UNTIL_CLOSED_DURATION_MS : detail.duration;

  // Forward the event's own id so that if a flow ever leaves the MFE's
  // shadow toaster enabled (no host anvil2 Dialog mounted), the host and
  // MFE toasts dedup on this id instead of double-firing.
  ToastModule.showToast({
    id: detail.id,
    status,
    title: detail.title,
    toastBody: detail.message,
    duration,
  });
}

// Multiple MFE adapters can be mounted at once (e.g. InboxPage keeps every
// previously selected task's CustomerMfeAdapter alive, CSS-hidden, to
// preserve state), each calling this hook. window listeners are global, so
// registering one per mounted instance would fan a single hammer-toast event
// out to one ToastModule.showToast() call per instance. Ref-count instead so
// exactly one listener is ever attached.
let listenerRefCount = 0;

/**
 * Bridges anvil2 `hammer-toast` window events to CCP's native ToastModule.
 *
 * anvil2's `toast.success()` / `toast.danger()` dispatch CustomEvents on
 * `window` with type `"hammer-toast"` and `composed: true`, so they cross the
 * shadow boundary of an embedded MFE and reach the host `window`. CCP doesn't
 * use anvil2's `<Toaster>`, so this hook catches those events and forwards them
 * to ToastModule.showToast(), and `toast.dismiss(id)` to ToastModule.hideToastById().
 *
 * Needed for shadow-DOM MFEs (customer-mfe, VA) because anvil2's own
 * `<Toaster>` silences itself when it isn't the first `[data-anv='toaster']` in
 * the light DOM — a host `dialog-toaster` always wins that check, so the MFE's
 * shadow toaster never renders (CCPE-1317).
 */
export default function useAnvil2ToastBridge(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    if (listenerRefCount === 0) {
      window.addEventListener('hammer-toast', handleHammerToast);
    }
    listenerRefCount++;

    return () => {
      listenerRefCount--;
      if (listenerRefCount === 0) {
        window.removeEventListener('hammer-toast', handleHammerToast);
      }
    };
  }, [enabled]);
}
